import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useDialog } from "../../../components/dialog/DialogContext";
import ManuallyMusteringModal from "../../../components/report/mustering/manually-mustering-modal";
import TableTab from "../../../components/tabs/table-tab";
import { FILTER_SITES } from "../../../constants/filter-types";
import { REPORTS_MUSTERING } from "../../../constants/menu-types";
import { GENERAL_MUSTERING_STATUS } from "../../../constants/report-types";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { DEF_PG_SIZE_REPORTS } from '../../../constants/maximum-page-size';
import TableContextStateMonitor from "../../../contexts/table/TableContextStateMonitor";
import MusteredReportColumns from "../../../models/ColumnDefinitions/MusteringReportColumns";
import { Filter } from "../../../models/Filter";
import { ListData } from "../../../models/ListData";
import { ListQuery } from "../../../models/ListQuery";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import { MultiSelectOption } from "../../../models/MultiSelectOption";
import { MusteringReportTotals } from "../../../models/MusteringReportTotals";
import { MusteringStatuses } from "../../../models/Status";
import CategorySearch from "../../../models/search/CategorySearch";
import MusteringReportSearch from "../../../models/search/MusteringReportSearch";
import EnrolmentSearch from "../../../models/search/EnrolmentSearch";
import SiteSearch from "../../../models/search/SiteSearch";
import { auditRunReport } from "../../../services/auditService";
import { getCategoryNameWithPagination } from "../../../services/categoryService";
import { getMusteringReport, getMusteringReportTotals } from "../../../services/reportService";
import { getSiteById, getSiteNamesWithPagination } from "../../../services/siteService";
import { getEnrolmentNamesWithPagination } from "../../../services/enrolmentService";
import { injectTransack } from "../../../services/transactionService";
import { getUserFiltersByView } from "../../../services/userPreferenceService";
import { getErrorMessage, showError } from "../../../services/utilities/ServiceError";
import { IAppState } from "../../../store/types";
import { appendFilter } from "../../../utilities/filters";

type Props = {
    reportStatus: number;
    reportId: string;
    setMusteringReportTotals: (musteringReportTotals: MusteringReportTotals) => void;
}

const MusteringReport: FC<Props> = ({ reportStatus, reportId, setMusteringReportTotals }) => {
    const { translate } = useLocale();
    const userSiteId = useSelector((state: IAppState) => (state.userInfo.siteId));
    const userLoginId = useSelector((state: any) => (state.userInfo.id));
    const [siteId, setSiteId] = useState(userSiteId);
    const dialog = useDialog();
    const [isOpen, setIsOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<any>({});
    const musteredStatus: number[] = [GENERAL_MUSTERING_STATUS.MANUALLY_MUSTERED, GENERAL_MUSTERING_STATUS.MUSTERED];
    const lookup: Lookup[] = [
        {
            name: LookupTypes.profile_id,
            values: [],
            dataMethod: getCategoryNameWithPagination,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.MusteringStatuses,
            values: MusteringStatuses(translate),
        },
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName,
            isSingleSelect: true
        },
        {
            name: "id",
            values: [],
            dataMethod: getEnrolmentNamesWithPagination,
            searchAccessor: EnrolmentSearch.find(column => column.accessor === "name")?.searchOnName
        }
    ];

    const getReport = async (listParameters?: ListQuery) => {
        try {
            await auditRunReport(reportId);
            if (reportStatus === GENERAL_MUSTERING_STATUS.ALL) {
                return await getMusteringReport(translate, listParameters);
            }
            else if (reportStatus === GENERAL_MUSTERING_STATUS.MUSTERED) {
                return await getMusteringReport(translate, {
                    ...listParameters,
                    filters: appendFilter(
                        [GENERAL_MUSTERING_STATUS.MUSTERED, GENERAL_MUSTERING_STATUS.MANUALLY_MUSTERED],
                        'Status',
                        'status',
                        listParameters?.filters ?? [])
                });
            }
            else {
                return await getMusteringReport(translate, {
                    ...listParameters,
                    filters: appendFilter(reportStatus, 'Status', 'status', listParameters?.filters ?? [])
                });
            }
        } catch (error) {
            return Promise.reject(getErrorMessage(error));
        }
    }

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    const initialiseUserPreferences = async (setDetails: (userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys) => void) => {
        const staticFilterKeys = ["site.id"];
        let customFilter: Filter[] = [];

        try {
            const viewPreference = await getUserFiltersByView(userLoginId, REPORTS_MUSTERING, reportId);
            const siteFilter = viewPreference.find(filter => filter.filterId === FILTER_SITES);

            //Add custom filter
            if (!!!siteFilter) {
                const site = await getSiteById(userSiteId);
                customFilter = [
                    {
                        label: translate.byKey(TranslationKeys.site),
                        key: "site.id",
                        isCustomFilter: true,
                        isStatic: true,
                        clearPolicy: "locked",
                        options: [],
                        selected: [
                            {
                                label: site.name,
                                value: site.id.toString()
                            }
                        ]
                    }
                ]
                setSiteId(site.id);
            }
            else if (siteFilter && siteFilter.selected.length > 0) {
                setSiteId(siteFilter.selected[0].value);
            }

        } catch (error) {
            showError(error, toast.warn);
        }
        finally {
            setDetails(userLoginId, REPORTS_MUSTERING, customFilter, reportId, staticFilterKeys);
        }
    }

    const getReportTitle = () => {
        if (reportStatus === GENERAL_MUSTERING_STATUS.ALL) {
            return translate.byKey(TranslationKeys.generalMusteringReport)
        }
        else if (reportStatus === GENERAL_MUSTERING_STATUS.ABSENT) {
            return translate.byKey(TranslationKeys.generalMusteringReportAbsentTab)
        }
        else if (reportStatus === GENERAL_MUSTERING_STATUS.MUSTERED) {
            return translate.byKey(TranslationKeys.generalMusteringReportMusteredTab)
        }
        else if (reportStatus === GENERAL_MUSTERING_STATUS.EXITED) {
            return translate.byKey(TranslationKeys.generalMusteringReportExitedTab)
        }
        else if (reportStatus === GENERAL_MUSTERING_STATUS.UNACCOUNTED_FOR) {
            return translate.byKey(TranslationKeys.generalMusteringReportUnaccountedForTab)
        }
    }

    const getColumns = () => {
        // hide lookup/filter on status column when on specific tabs
        // since specific tabs will display only one status
        (reportStatus === GENERAL_MUSTERING_STATUS.ALL)
            ? MusteredReportColumns[3].hideLookup = false
            : MusteredReportColumns[3].hideLookup = true

        return MusteredReportColumns;
    }

    const onSave = async (values: any) => {
        injectTransack(values.id, values.musteringPointId, false).then(() => {
            refreshSummarySection(siteId);
            dataRefresh(getDataMethod);
        }).catch((error) => {
            dialog.pushError({
                title: translate.byKey(TranslationKeys.error),
                message: getErrorMessage(error),
            });
        }).finally(() => {
            setIsOpen(false);
        })
    }

    const refreshSummarySection = async (siteId: string) => {
        const totals = await getMusteringReportTotals(siteId, +reportId);
        setMusteringReportTotals(totals);
    }

    const onFilterChange = (filters: Filter[]) => {
        const siteFilter = filters.find(filter => filter.key === 'site.id');
        if (siteFilter && siteFilter.selected.length > 0) {
            const site: MultiSelectOption = siteFilter.selected[0];
            setSiteId(site.value);
            refreshSummarySection(site.value);
        }
    }

    const manuallyMuster = (rowData) => {
        if (!musteredStatus.includes(rowData.statusValue)) {
            setCurrentItem(rowData);
            setIsOpen(true);
        }
    }

    const getDataMethod = async (listParameters?: ListQuery) => {
        try {
            await auditRunReport(reportId);
            refreshSummarySection(siteId);
            return getReport(listParameters);
        } catch (error) {
            return Promise.reject(getErrorMessage(error));
        }
    }

    return <>
        <TableContextProvider
            maxPageSize={DEF_PG_SIZE_REPORTS}
            dataMethod={getDataMethod}
            initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
            manualDataRefresh={manualDataRefresh}
            tableDescriptor={getColumns()}
            searchDescriptor={MusteringReportSearch}
            useFilter
            useSort
            usePagination
            useSearch
            useTable
            customMusteringWithRange={{ fieldName: "statusValue", excludedValues: musteredStatus }}
            manuallyMuster={manuallyMuster}
            lookup={lookup}
            loadDataOnDemand
            runDataOnDemandButtonLabel={translate.byKey(TranslationKeys.runReport)}>
            <TableTab
                title={''}
                reportTitle={getReportTitle()}
                selectRows={false}
                onRowClicked={manuallyMuster}
                cyName="musteringReport">
                <TableContextStateMonitor onFilterChange={onFilterChange} />
            </TableTab>
        </TableContextProvider >
        <ManuallyMusteringModal isOpen={isOpen} onClose={() => setIsOpen(false)} item={currentItem} siteId={siteId} onSave={onSave} />
    </>
};

export default MusteringReport;

